/**
 * Flight status page for tools interface.
 * Reuses the same UI as the lawyer flight-status page.
 */
import { useState } from 'react'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { Plane, Search, Loader2, AlertCircle, ArrowLeft } from 'lucide-react'
import { toolsRoute } from './index'
import { flightStatusAPI } from '@/utils/api/lawyerAPI'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/**
 * Format a date as DDMMM for Amadeus (e.g., "01JAN", "15DEC")
 */
function formatDateForAmadeus(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0')
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
  const month = months[date.getMonth()]
  return `${day}${month}`
}

function ToolsFlightStatusPage() {
  const navigate = useNavigate()
  const [airline, setAirline] = useState('')
  const [flightNumber, setFlightNumber] = useState('')
  const [date, setDate] = useState(formatDateForAmadeus(new Date()))
  const [isLoading, setIsLoading] = useState(false)
  const [response, setResponse] = useState<string | null>(null)
  const [command, setCommand] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setResponse(null)
    setCommand(null)

    if (!airline.trim()) {
      setError('Bitte geben Sie einen Airline-Code ein')
      return
    }
    if (airline.trim().length !== 2) {
      setError('Der Airline-Code muss 2 Zeichen haben (z.B. LH, EW, OS)')
      return
    }
    if (!flightNumber.trim()) {
      setError('Bitte geben Sie eine Flugnummer ein')
      return
    }

    setIsLoading(true)
    try {
      const result = await flightStatusAPI.getFlightStatus(
        airline.trim(),
        flightNumber.trim(),
        date.trim()
      )
      setCommand(result.command)
      if (result.error) {
        setError(result.error)
      } else {
        setResponse(result.response)
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Unbekannter Fehler'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate({ to: '/tools' })}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Zurück zur Übersicht
      </button>

      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
          <Plane className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Flugstatus</h1>
          <p className="text-slate-500">Flugstatus und Informationen abrufen</p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 min-w-0">
            <Label htmlFor="airline" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Airline
            </Label>
            <Input
              id="airline"
              type="text"
              placeholder="LH"
              value={airline}
              onChange={(e) => setAirline(e.target.value.toUpperCase())}
              maxLength={2}
              className="uppercase w-full"
            />
          </div>

          <div className="flex-1 min-w-0">
            <Label htmlFor="flightNumber" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Flugnummer
            </Label>
            <Input
              id="flightNumber"
              type="text"
              placeholder="123"
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value.replace(/\D/g, ''))}
              className="w-full"
            />
          </div>

          <div className="flex-1 min-w-0">
            <Label htmlFor="date" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Datum (TTMMM)
            </Label>
            <Input
              id="date"
              type="text"
              placeholder="15DEC"
              value={date}
              onChange={(e) => setDate(e.target.value.toUpperCase())}
              maxLength={5}
              className="uppercase w-full"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full md:w-auto"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Abfrage...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Abfragen
              </>
            )}
          </Button>
        </form>

        <p className="text-xs text-slate-400 mt-3">
          Beispiel: LH 123 am 15DEC
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Fehler</p>
            <p className="text-red-700 text-sm">{error}</p>
            {command && (
              <p className="text-red-600 text-xs mt-1 font-mono">Befehl: {command}</p>
            )}
          </div>
        </div>
      )}

      {/* Response */}
      {response && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Amadeus Antwort</span>
            {command && (
              <code className="text-xs bg-slate-200 px-2 py-1 rounded text-slate-600">
                {command}
              </code>
            )}
          </div>
          <pre className="p-4 overflow-x-auto text-sm font-mono whitespace-pre leading-relaxed bg-slate-900 text-green-400">
            {response}
          </pre>
        </div>
      )}

      {/* Empty state when no query yet */}
      {!response && !error && !isLoading && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center">
          <Plane className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">
            Geben Sie eine Airline und Flugnummer ein, um den Flugstatus abzurufen
          </p>
        </div>
      )}
    </div>
  )
}

export const toolsFlightStatusRoute = createRoute({
  getParentRoute: () => toolsRoute,
  path: '/flight-status',
  component: ToolsFlightStatusPage,
})
